/**
 * Guest User Service
 * 
 * Handles all guest user specific logic and API interactions.
 * Guest users have full functionality but cannot log back in after logout.
 */

import { platformApi } from './platformApi';
import type { User } from '../types/api/user';

export interface GuestUserData {
  userId: string;
  username: string;
  sessionExpiry: string;
  token: string;
}

export interface GuestProfile extends User {
  isGuest: true;
  sessionExpiry: string;
}

class GuestUserService {
  private readonly GUEST_PROFILE_KEY = 'vaiu_guest_profile';
  private readonly GUEST_SESSION_KEY = 'vaiu_guest_session';
  
  /**
   * Check if a token belongs to a guest user
   */
  isGuestToken(token: string): boolean {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return false;
      
      const payload = JSON.parse(atob(parts[1]));
      return payload.isGuest === true;
    } catch {
      return false;
    }
  }

  /**
   * Get the guest session expiry from token
   */
  getSessionExpiry(token: string): Date | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      
      const payload = JSON.parse(atob(parts[1]));
      if (payload.exp) {
        return new Date(payload.exp * 1000);
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Create a minimal guest profile when API profile fetch fails
   */
  createMinimalGuestProfile(guestData: GuestUserData): GuestProfile {
    const now = new Date().toISOString();
    
    return {
      id: guestData.userId,
      username: guestData.username,
      email: '',
      isGuest: true,
      roles: ['USER'],
      balances: {
        USD: 0,
        EUR: 0,
        GBP: 0,
        BTC: 0
      },
      sessionExpiry: guestData.sessionExpiry,
      createdAt: now,
      updatedAt: now,
      preferences: {
        language: 'en',
        currency: 'USD',
        notifications: {
          email: false,
          push: false,
          sms: false
        }
      }
    } as GuestProfile;
  }

  /**
   * Store guest profile locally for offline access
   */
  storeGuestProfile(profile: GuestProfile): void {
    localStorage.setItem(this.GUEST_PROFILE_KEY, JSON.stringify(profile));
  }

  /**
   * Retrieve stored guest profile
   */
  getStoredGuestProfile(): GuestProfile | null {
    try {
      const stored = localStorage.getItem(this.GUEST_PROFILE_KEY);
      if (stored) {
        return JSON.parse(stored) as GuestProfile;
      }
    } catch (error) {
      console.error('Failed to retrieve stored guest profile:', error);
    }
    return null;
  }

  /**
   * Store guest session data
   */
  storeGuestSession(sessionData: GuestUserData): void {
    localStorage.setItem(this.GUEST_SESSION_KEY, JSON.stringify({
      ...sessionData,
      createdAt: new Date().toISOString()
    }));
  }

  /**
   * Get stored guest session
   */
  getStoredGuestSession(): GuestUserData | null {
    try {
      const stored = localStorage.getItem(this.GUEST_SESSION_KEY);
      if (stored) {
        const session = JSON.parse(stored);
        
        // Check if session has expired
        const expiry = new Date(session.sessionExpiry);
        if (expiry > new Date()) {
          return session as GuestUserData;
        } else {
          // Session expired, clean up
          this.clearGuestData();
        }
      }
    } catch (error) {
      console.error('Failed to retrieve guest session:', error);
    }
    return null;
  }

  /**
   * Check if guest session is about to expire (within 1 hour)
   */
  isSessionExpiringSoon(): boolean {
    const session = this.getStoredGuestSession();
    if (!session) return false;
    
    const expiry = new Date(session.sessionExpiry);
    const now = new Date();
    const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1000);
    
    return expiry <= oneHourFromNow;
  }

  /**
   * Get time remaining in session
   */
  getSessionTimeRemaining(): {
    hours: number;
    minutes: number;
    expired: boolean;
  } {
    const session = this.getStoredGuestSession();
    if (!session) {
      return { hours: 0, minutes: 0, expired: true };
    }
    
    const expiry = new Date(session.sessionExpiry);
    const now = new Date();
    const diff = expiry.getTime() - now.getTime();
    
    if (diff <= 0) {
      return { hours: 0, minutes: 0, expired: true };
    }
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return { hours, minutes, expired: false };
  }

  /**
   * Fetch guest profile with fallback to minimal profile
   */
  async fetchGuestProfile(token: string, guestData?: GuestUserData): Promise<GuestProfile> {
    try {
      // Try to fetch the actual profile from the backend
      const profile = await platformApi.getProfile();
      
      // Add guest-specific fields
      const guestProfile: GuestProfile = {
        ...profile,
        isGuest: true,
        sessionExpiry: guestData?.sessionExpiry || this.getSessionExpiry(token)?.toISOString() || ''
      };
      
      // Store for offline access
      this.storeGuestProfile(guestProfile);
      
      return guestProfile;
    } catch (error) {
      console.warn('Failed to fetch guest profile from backend, using minimal profile:', error);
      
      // If we have guest data, create and store a minimal profile
      if (guestData) {
        const minimalProfile = this.createMinimalGuestProfile(guestData);
        this.storeGuestProfile(minimalProfile);
        return minimalProfile;
      }
      
      // Try to use stored profile
      const storedProfile = this.getStoredGuestProfile();
      if (storedProfile) {
        return storedProfile;
      }
      
      // Last resort: create profile from token
      const expiry = this.getSessionExpiry(token);
      const fallbackData: GuestUserData = {
        userId: `guest_${Date.now()}`,
        username: `guest_${Date.now()}`,
        sessionExpiry: expiry?.toISOString() || new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString(),
        token
      };
      
      return this.createMinimalGuestProfile(fallbackData);
    }
  }

  /**
   * Update guest profile locally and attempt to sync with backend
   */
  async updateGuestProfile(updates: Partial<User>): Promise<GuestProfile> {
    const currentProfile = this.getStoredGuestProfile();
    if (!currentProfile) {
      throw new Error('No guest profile found');
    }
    
    const updatedProfile: GuestProfile = {
      ...currentProfile,
      ...updates,
      updatedAt: new Date().toISOString()
    };
    
    // Store locally immediately
    this.storeGuestProfile(updatedProfile);
    
    // Try to sync with backend (non-blocking)
    platformApi.updateProfile(updates).catch(error => {
      console.warn('Failed to sync guest profile with backend:', error);
      // Profile is already updated locally, so we continue
    });
    
    return updatedProfile;
  }

  /**
   * Enable all features for guest users by intercepting API calls
   */
  enableGuestFeatures(): void {
    // Store original methods
    const originalGet = platformApi.profile.get;
    const originalPost = platformApi.profile.post;
    const originalPut = platformApi.profile.put;
    
    // Override methods to handle guest-specific cases
    // Use a closure to capture 'this' (the GuestUserService instance)
    const self = this;
    platformApi.profile.get = async function(this: any, ...args: any[]) {
      try {
        return await originalGet.apply(this, args);
      } catch (error: any) {
        // If it's a 401 for a guest user, handle gracefully
        if (error.message?.includes('401') && self.isGuestToken(platformApi.getToken() || '')) {
          console.warn('Guest user API call failed, using fallback:', args[0]);
          
          // Return appropriate fallback data based on endpoint
          const endpoint = args[0] as string;
          
          if (endpoint.includes('/profile')) {
            return self.getStoredGuestProfile();
          }
          
          if (endpoint.includes('/preferences')) {
            return self.getStoredGuestProfile()?.preferences || {};
          }
          
          if (endpoint.includes('/balance')) {
            return self.getStoredGuestProfile()?.balances || {};
          }
          
          // For other endpoints, throw the error
          throw error;
        }
        throw error;
      }
    }.bind(platformApi.profile);
  }

  /**
   * Clear all guest data
   */
  clearGuestData(): void {
    localStorage.removeItem(this.GUEST_PROFILE_KEY);
    localStorage.removeItem(this.GUEST_SESSION_KEY);
    localStorage.removeItem('vaiu-brand-preference');
  }

  /**
   * Convert guest to permanent user
   */
  async upgradeGuestUser(registrationData: {
    email: string;
    password: string;
    username?: string;
  }): Promise<{
    success: boolean;
    token?: string;
    message?: string;
  }> {
    try {
      const currentToken = platformApi.getToken();
      if (!currentToken || !this.isGuestToken(currentToken)) {
        throw new Error('Not a guest user');
      }
      
      // Call the upgrade endpoint
      const response = await platformApi.auth.post('/guest/upgrade', {
        ...registrationData,
        guestToken: currentToken
      });
      
      if (response.token) {
        // Clear guest data
        this.clearGuestData();
        
        // Set new token
        platformApi.setToken(response.token);
      }
      
      return response;
    } catch (error: any) {
      console.error('Failed to upgrade guest user:', error);
      return {
        success: false,
        message: error.message || 'Failed to upgrade account'
      };
    }
  }

  /**
   * Check if upgrade is available for current guest
   */
  canUpgrade(): boolean {
    const token = platformApi.getToken();
    if (!token) return false;
    
    // Check if it's a guest token
    if (!this.isGuestToken(token)) return false;
    
    // Check if session hasn't expired
    const timeRemaining = this.getSessionTimeRemaining();
    return !timeRemaining.expired;
  }
}

// Export singleton instance
export const guestUserService = new GuestUserService();

// Auto-enable guest features on load
if (typeof window !== 'undefined') {
  guestUserService.enableGuestFeatures();
}
